import { ReactElement } from 'react';
import classNames from 'classnames';
import { Pill } from '@sc/shared-ui';
import { PlusIcon } from '@heroicons/react/24/outline';

/**
 * Sentinel `value` representing the "Add new" option. Consumers compare the
 * selected value against this to branch into their new-card flow.
 */
export const ADD_NEW_PAYMENT_METHOD = 'add-new';

/**
 * The subset of a saved card the selector needs to render. The
 * `listPaymentMethods` query result structurally satisfies this.
 */
export interface SelectablePaymentMethod {
  uuid: string;
  brand: string;
  last4: string;
  useForMonthlyPlan: boolean;
}

interface Props {
  paymentMethods: SelectablePaymentMethod[];
  /** The selected card's `uuid`, or {@link ADD_NEW_PAYMENT_METHOD}. */
  value: string;
  onChange: (value: string) => void;
  /** Validation message rendered beneath the list. */
  error?: string | undefined;
  label?: string;
  /** Render the trailing "Add new" option. Defaults to `true`. */
  showAddNew?: boolean;
  /** Extra classes for the outer wrapper (e.g. spacing). */
  className?: string;
}

const optionClass = (selected: boolean): string =>
  classNames(
    'flex items-center gap-3 px-3 py-3 rounded-xs text-left w-full border transition-colors',
    selected ? 'border-white' : 'border-grey-t-700 hover:border-white'
  );

const RadioDot = ({ checked }: { checked: boolean }): ReactElement => (
  <span
    className={classNames(
      'size-4 rounded-full border shrink-0',
      checked ? 'border-5 border-primary-400' : 'border-grey-400'
    )}
    aria-hidden="true"
  />
);

/**
 * Controlled picker for choosing a saved card (or adding a new one).
 * Local-state callers wire `value`/`onChange` to `useState`.
 */
export const PaymentMethodSelector = ({
  paymentMethods,
  value,
  onChange,
  error,
  label = 'Select payment method',
  showAddNew = true,
  className,
}: Props): ReactElement => (
  <div className={classNames('flex flex-col gap-2', className)}>
    <span className="font-semibold text-body-medium">{label}</span>
    <div className="flex flex-col gap-2">
      {paymentMethods.map((m) => {
        const isSelected = value === m.uuid;
        const isDefault = m.useForMonthlyPlan;

        return (
          <button
            type="button"
            key={m.uuid}
            onClick={() => onChange(m.uuid)}
            className={optionClass(isSelected)}
          >
            <span className="flex-1 text-body-medium text-white capitalize">
              {m.brand} ending in {m.last4}
            </span>
            {isDefault && (
              <Pill
                size="small"
                colour="#55C78E"
                backgroundColour="#151F1E"
                outline
                text="Default"
              />
            )}
            <RadioDot checked={isSelected} />
          </button>
        );
      })}
      {showAddNew && (
        <button
          type="button"
          onClick={() => onChange(ADD_NEW_PAYMENT_METHOD)}
          className={optionClass(value === ADD_NEW_PAYMENT_METHOD)}
        >
          <PlusIcon className="size-5 shrink-0 text-white" aria-hidden="true" />
          <span className="flex-1 text-body-medium text-white">Add new</span>
          <RadioDot checked={value === ADD_NEW_PAYMENT_METHOD} />
        </button>
      )}
    </div>
    {error && <span className="text-sm text-red-300">{error}</span>}
  </div>
);
